<script setup lang="ts">
import { ref, computed } from 'vue';
import useHeicConverter from '@ds/composables/useHeicConverter';

interface ComposedFile extends File {
	name: string;
	url: string;
	isPdf: boolean;
	isHeic: boolean;
}

const emit = defineEmits(['addFiles', 'updateFiles']);
defineProps<{ singleFile?: boolean }>();

const input = ref<HTMLInputElement | null>(null);
const rawFiles = ref<File[]>([]); // Original files to be send to the server

const { convertedFiles, isConvertingFiles, errorConvertingFiles } = useHeicConverter<File>({
	files: rawFiles,
});
/**
 * PDF and HEIC(to jpeg) for preview
 * If upload images in safari while allwoing heic extension
 * all images get converted to heic but its natively supported
 * https://forums.developer.apple.com/forums/thread/743049
 */
const composedFiles = computed((): ComposedFile[] => {
	return rawFiles.value.map((file: File) => {
		const isPdf = file.type?.includes('pdf');
		const isHeic = file.type?.includes('heic');

		return {
			...file,
			name: file.name,
			url: URL.createObjectURL(file),
			isPdf,
			isHeic,
		};
	});
});

const handleUploadedFiles = async (event: Event) => {
	const { files } = event.target as HTMLInputElement;
	rawFiles.value = [...(files as FileList)];
	emit('addFiles', rawFiles.value);
};

const handleDeleteFile = (event: Event, fileName: string) => {
	event.preventDefault();

	rawFiles.value = rawFiles.value.filter((file: File) => file.name !== fileName);
	emit('updateFiles', rawFiles.value);
};
</script>
<template>
	<div data-test="uploadFiles">
		<label
			tabindex="0"
			class="upload-document-btn items-center px-2 bg-warning-400 border-radius-2 text-weight-medium cursor-pointer"
			@keydown.enter.prevent="(input as HTMLInputElement)?.click"
		>
			<span class="bx bx-upload mr-2" />
			<span>Upload documents</span>
			<input
				ref="input"
				type="file"
				accept="image/*,.pdf,.heic"
				:multiple="!singleFile"
				data-test="uploadFilesInput"
				@change="handleUploadedFiles"
			/>
		</label>
		<div
			v-for="(file, index) in composedFiles"
			:key="`file-${index}`"
			data-test="uploadFilesList"
			class="flex items-center justify-between bg-neutral-100 border-radius-2 p-2 mt-2"
		>
			<span class="file-name text truncate pl-4 pr-8">
				{{ file.name }}
			</span>
			<object v-if="file.isPdf" :data="file.url" type="application/pdf" class="file-preview">
				uploaded file
			</object>
			<div v-else-if="file.isHeic && isConvertingFiles" class="img-placeholder img-loader" />
			<svg
				v-else-if="file.isHeic && errorConvertingFiles"
				xmlns="http://www.w3.org/2000/svg"
				fill="none"
				viewBox="0 0 24 24"
				stroke-width="1.5"
				stroke="red"
				style="width: 80; height: 52"
			>
				<path
					stroke-linecap="round"
					stroke-linejoin="round"
					d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
				/>
			</svg>
			<img
				v-else
				:src="
					file.isHeic ? (convertedFiles as ComposedFile[])?.find((f) => f?.name === file.name)?.url : file.url
				"
				alt=""
				class="file-preview"
			/>
			<button
				aria-label="Remove uploaded file"
				title="Remove uploaded file"
				class="justify-end px-3 mr-2"
				@click.stop="handleDeleteFile($event, file.name)"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					viewBox="0 0 24 24"
					style="fill: black"
				>
					<path
						d="m16.192 6.344-4.243 4.242-4.242-4.242-1.414 1.414L10.535 12l-4.242 4.242 1.414 1.414 4.242-4.242 4.243 4.242 1.414-1.414L13.364 12l4.242-4.242z"
					></path>
				</svg>
			</button>
		</div>
	</div>
</template>
<style lang="scss" scoped>
.upload-document-btn {
	display: inline-flex;
	height: $height-form-element;
	border: $border-width-1 $border-style transparent;

	@media (hover: hover) {
		&:hover {
			background-color: $color-warning-500;
		}
	}

	&:focus-visible {
		outline: $border-width-2 $border-style $color-focus;
	}

	input {
		display: none;
	}
}

.file-name {
	width: 100%;
	overflow-wrap: break-word;
}

.file-preview,
.img-placeholder {
	width: 80px;
	height: 52px;
	object-fit: cover;
	border-radius: $border-radius-1;
}
</style>
